import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Typography,
    Container,
    Skeleton,
    Chip,
    Button,
    Grid,
    Breadcrumbs,
    Link as MuiLink
} from "@mui/material";
import { useNavigate, Link } from "react-router-dom";
import BogoCard from "../../Components/BogoCard/BogoCard";
import { getUser } from "../../Utils/authHelper";

const rubikfont = { fontFamily: "Rubik, sans-serif" };
const API_URL = "https://api.glamcompany.in/api/v1/";

const BogoList = () => {
    const [bogos, setBogos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedGroupKey, setSelectedGroupKey] = useState("all");
    const navigate = useNavigate();

    const user = getUser();
    const isBusiness = user?.customerType === "business";

    useEffect(() => {
        window.scrollTo(0, 0);
        const fetchBogos = async () => {
            try {
                const res = await fetch(`${API_URL}bogo-offer/all`);
                const data = await res.json();
                if (data.success) {
                    const rawList = data.data || [];
                    const filtered = rawList.filter(item => {
                        if (!item.visibility || item.visibility.length === 0) return true;
                        if (isBusiness) return item.visibility.includes("b2b");
                        return item.visibility.includes("b2c");
                    });
                    setBogos(filtered);
                }
            } catch (e) {
                console.error("Failed to fetch BOGO offers:", e);
            } finally {
                setLoading(false);
            }
        };
        fetchBogos();
    }, [isBusiness]);

    const groups = useMemo(() => {
        const map = {};
        bogos.forEach((bogo) => {
            const buyQty = Number(bogo.buyQuantity) || 1;
            const getQty = Number(bogo.getQuantity) || 1;
            const key = `buy_${buyQty}_get_${getQty}`;

            if (!map[key]) {
                map[key] = {
                    key,
                    buyQuantity: buyQty,
                    getQuantity: getQty,
                    title: `Buy ${buyQty} Get ${getQty} Free`,
                    shortTitle: `Buy ${buyQty} Get ${getQty}`,
                    items: []
                };
            }
            map[key].items.push(bogo);
        });

        return Object.values(map).sort((a, b) => {
            if (a.buyQuantity !== b.buyQuantity) {
                return a.buyQuantity - b.buyQuantity;
            }
            return a.getQuantity - b.getQuantity;
        });
    }, [bogos]);

    const displayedGroups = selectedGroupKey === "all"
        ? groups
        : groups.filter(g => g.key === selectedGroupKey);

    return (
        <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: "#fafafa", minHeight: "80vh" }}>
            <Container maxWidth="xl">
                {/* Breadcrumbs */}
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 4, "& .MuiBreadcrumbs-separator": { color: "#C8A054" } }}>
                    <Link to="/" style={{ textDecoration: "none", color: "#666", ...rubikfont, fontSize: "14px", fontWeight: 500 }}>
                        Home
                    </Link>
                    <Typography color="#111" sx={{ ...rubikfont, fontSize: "14px", fontWeight: 600 }}>
                        Special Deals
                    </Typography>
                </Breadcrumbs>

                {/* Header */}
                <Box sx={{ mb: 4, pb: 3, borderBottom: "1px solid #e0e0e0" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box sx={{ width: "4px", height: "32px", bgcolor: "#C8A054", borderRadius: "4px" }} />
                        <Box>
                            <Typography sx={{ fontSize: "11px", fontWeight: 600, letterSpacing: "3px", color: "#C8A054", textTransform: "uppercase", mb: 0.5, ...rubikfont }}>
                                Exclusive Offers
                            </Typography>
                            <Typography sx={{ fontSize: { xs: "28px", sm: "40px" }, fontWeight: 800, color: "#111", fontFamily: "'Playfair Display', serif", lineHeight: 1 }}>
                                Special Deals
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Group Filter Tabs */}
                {!loading && groups.length > 1 && (
                    <Box sx={{ display: "flex", gap: 1.5, overflowX: "auto", pb: 2, mb: 4, scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
                        <Button
                            onClick={() => setSelectedGroupKey("all")}
                            sx={{
                                px: 3,
                                py: 1,
                                borderRadius: "30px",
                                textTransform: "none",
                                fontSize: "14px",
                                fontWeight: selectedGroupKey === "all" ? 600 : 500,
                                bgcolor: selectedGroupKey === "all" ? "#C8A054" : "#fff",
                                color: selectedGroupKey === "all" ? "#fff" : "#444",
                                border: selectedGroupKey === "all" ? "1px solid #C8A054" : "1px solid #e0e0e0",
                                transition: "all 0.2s ease",
                                whiteSpace: "nowrap",
                                boxShadow: selectedGroupKey === "all" ? "0 4px 12px rgba(200,160,84,0.3)" : "none",
                                ...rubikfont,
                                "&:hover": {
                                    bgcolor: selectedGroupKey === "all" ? "#b38841" : "#f5f5f5",
                                }
                            }}
                        >
                            All Deals ({bogos.length})
                        </Button>
                        {groups.map((group) => {
                            const isSelected = selectedGroupKey === group.key;
                            return (
                                <Button
                                    key={group.key}
                                    onClick={() => setSelectedGroupKey(group.key)}
                                    sx={{
                                        px: 3,
                                        py: 1,
                                        borderRadius: "30px",
                                        textTransform: "none",
                                        fontSize: "14px",
                                        fontWeight: isSelected ? 600 : 500,
                                        bgcolor: isSelected ? "#C8A054" : "#fff",
                                        color: isSelected ? "#fff" : "#444",
                                        border: isSelected ? "1px solid #C8A054" : "1px solid #e0e0e0",
                                        transition: "all 0.2s ease",
                                        whiteSpace: "nowrap",
                                        boxShadow: isSelected ? "0 4px 12px rgba(200,160,84,0.3)" : "none",
                                        ...rubikfont,
                                        "&:hover": {
                                            bgcolor: isSelected ? "#b38841" : "#f5f5f5",
                                        }
                                    }}
                                >
                                    {group.title} ({group.items.length})
                                </Button>
                            );
                        })}
                    </Box>
                )}

                {/* Loading skeleton */}
                {loading ? (
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 3 }}>
                        {Array.from({ length: 8 }).map((_, i) => (
                            <Box key={i}>
                                <Skeleton variant="rectangular" height={320} sx={{ borderRadius: "16px", mb: 2 }} />
                                <Skeleton width="80%" height={24} sx={{ mb: 1 }} />
                                <Skeleton width="50%" height={24} />
                            </Box>
                        ))}
                    </Box>
                ) : (
                    displayedGroups.length > 0 ? (
                        displayedGroups.map((group) => (
                            <Box key={group.key} sx={{ mb: 6 }}>
                                {/* Group Sub-heading */}
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                                    <Typography sx={{ fontSize: { xs: "20px", md: "24px" }, fontWeight: 700, color: "#111", textTransform: "capitalize", fontFamily: "'Playfair Display', serif" }}>
                                        {group.title}
                                    </Typography>
                                    <Chip
                                        label={`${group.items.length} ${group.items.length === 1 ? 'Offer' : 'Offers'}`}
                                        size="small"
                                        sx={{
                                            height: "24px",
                                            fontSize: "12px",
                                            fontWeight: 600,
                                            bgcolor: "#fdf8ee",
                                            color: "#C8A054",
                                            border: "1px solid rgba(200,160,84,0.3)",
                                            ...rubikfont
                                        }}
                                    />
                                </Box>

                                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 3 }}>
                                    {group.items.map((bogo) => (
                                        <BogoCard key={bogo._id} bogo={bogo} scrollTop />
                                    ))}
                                </Box>
                            </Box>
                        ))
                    ) : (
                        <Box sx={{ textAlign: "center", py: 10 }}>
                            <Typography sx={{ fontSize: "24px", fontWeight: 600, color: "#111", mb: 2, ...rubikfont }}>
                                No Special Deals Available
                            </Typography>
                            <Typography sx={{ fontSize: "16px", color: "#666", ...rubikfont }}>
                                Check back later for new exciting offers!
                            </Typography>
                        </Box>
                    )
                )}
            </Container>
        </Box>
    );
};

export default BogoList;
